/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';

export const Count = ({ time = '00', metric }) => {
  return (
    <div>
      {time} <span>{metric}</span>
      {/* {time} <span>{time > 1 ? metric : metric.slice(0, -1)}</span> */}
    </div>
  );
};

/**
 * Convert a time string to seconds.
 * @param {string} timeStr The time string to convert.
 * @returns {number} The time in seconds.
 */
const timeToSeconds = (timeStr) => {
  const [h, m, s] = timeStr.split(':').map(Number);
  return h * 3600 + m * 60 + s;
}

/**
 * Calculate the difference between two time strings in the format "HH:MM:SS".
 * @param {string} start The starting time string.
 * @param {string} current The current time string.
 * @returns {{ HH: number, MM: number, SS: number }} The difference between the start and current time.
 */
export const timeDiff = (start, current) => {
  // Calculate time difference in seconds
  const currentSeconds = timeToSeconds(current);
  const startSeconds = timeToSeconds(start);
  const timeDiffSeconds = startSeconds - currentSeconds;

  // Convert time difference back to hours, minutes, and seconds
  const hours = Math.floor(timeDiffSeconds / 3600);
  const remainingSeconds = timeDiffSeconds % 3600;
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  return {
    HH: Number(hours.toString().padStart(2, '0')),
    MM: Number(minutes.toString().padStart(2, '0')),
    SS: Number(seconds.toString().padStart(2, '0')),
  };
}

/**
 * Calculate the time left until the event.
 * @param {Date} countdownTo The date and time of the event.
 * @returns {{ Days: number, Hours: number, Minutes: number, Seconds: number }} Time left until the event.
 */
export const calculateEventTimeLeft = (countDownTo) => {
  let timeLeft = {
    Days: 0,
    Hours: 0,
    Minutes: 0,
    Seconds: 0,
  };

  if (countDownTo) {
    const now = new Date();
    const diff = +new Date(countDownTo) - +now;
    if (diff > 0) {
      timeLeft = {
        Days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        Hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        Minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        Seconds: Math.floor((diff % (1000 * 60)) / 1000),
      };
    }
  }

  return timeLeft;
};

/**
 * Custom hook to calculate and update the time left until an event.
 * @param {Date} timerCountdownTo The date and time of the event to countdown to.
 * @returns {{ Days: number, Hours: number, Minutes: number, Seconds: number }} Time left until the event.
 */
export const useEventTimer = (inForm, timerCountdownTo) => {
  const [timeLeft, setTimeLeft] = useState(calculateEventTimeLeft(timerCountdownTo));

  useEffect(() => {
    let intervalId;
    intervalId = setInterval(() => {
      setTimeLeft(calculateEventTimeLeft(timerCountdownTo));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [timerCountdownTo]);

  return timeLeft;
};

/**
 * Calculate the time left for the quiz timer.
 * @param {boolean} inForm Indicates whether the timer should be active.
 * @param {{ HH?: number, MM?: number, SS?: number }} timer The initial timer values.
 * @param {boolean} [pauseTime=false] Indicates whether the timer should be paused.
 * @returns {{ Hours: number, Minutes: number, Seconds: number }} Time left for the quiz timer.
 */
export const useQuizTimer = (inForm, timer, pauseTime = false) => {
  const initialTimeLeft = {
    Hours: timer.HH || 0,
    Minutes: timer.MM || 0,
    Seconds: timer.SS || 0,
  };

  const [timeLeft, setTimeLeft] = useState(initialTimeLeft);

  useEffect(() => {
    let intervalId;
    if (inForm) {
      intervalId = setInterval(() => {
        setTimeLeft((prevTimeLeft) => {
          let { Hours, Minutes, Seconds } = prevTimeLeft;

          if (!pauseTime) {
            if (Seconds > 0) {
              Seconds--;
            } else {
              if (Minutes > 0) {
                Minutes--;
                Seconds = 59;
              } else {
                if (Hours > 0) {
                  Hours--;
                  Minutes = 59;
                  Seconds = 59;
                }
              }
            }
          }
          return { Hours, Minutes, Seconds };
        });
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [inForm, pauseTime]);

  return inForm ? timeLeft : initialTimeLeft;
};
